import apiClient from '@/services/api/apiClient';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PreCallBrief {
  callObjective: string;
  openingScript: string;
  keyThingsToEstablish: string[];
  talkTrack: Array<{ topic: string; whatToSay: string; transitionTo: string }>;
  objectionPlaybook: Record<string, string>;
  questionsToAsk: Array<{ question: string; purpose: string; when: string }>;
  minuteByMinuteGuide: Array<{ minutes: string; focus: string; goal: string }>;
  redFlags: string[];
  closeMove: string;
}

export interface CoachingPrompts {
  stageFocus: string;
  promptCards: Array<{
    id: number;
    title: string;
    trigger: string;
    phrase: string;
    probeQuestion: string;
    goal: string;
  }>;
  dangerSignals: string[];
  buyingSignalPhrases: string[];
  pivotMove: string;
}

export interface PostCallAnalysis {
  outcome: string;
  callSummary: string;
  confirmedFacts: Array<{ fact: string; category: string }>;
  newInsights: string[];
  objectionsRaised: Array<{ objection: string; response: string; resolved: boolean }>;
  stakeholdersIdentified: Array<{ name: string; role: string; influence: string }>;
  commitmentsMade: Array<{ who: string; commitment: string; deadline: string }>;
  stageAdvancement: { recommended: boolean; direction: string; newStage: number; reason: string };
  journeyFieldUpdates: { activities: string; response: string; nextSteps: string; notes: string };
  urgencyScore: number;
  nextCallRecommendation: { timing: string; objective: string; prepNeeded: string };
  dealHealth: string;
  dealHealthReason: string;
  _journeyUpdated?: boolean;
}

export interface ExtractedStageData {
  activities: string;
  response: string;
  nextSteps: string;
  notes: string;
  trigger?: string;
  confidence: 'high' | 'medium' | 'low';
  missingInfo: string[];
  _saved?: boolean;
}

export interface NextCallBrief {
  callContext: string;
  openingLine: string;
  agendaToPropose: string[];
  unresolvedObjections: string[];
  hypothesesToValidate: string[];
  contentToSendBeforeCall: string;
  successMetric: string;
  riskToMonitor: string;
}

export interface ScheduledCall {
  taskId: string;
  contactId: string;
  contactName: string;
  accountName: string;
  industry: string;
  region: string;
  callType: string;
  dueDate: string;
  assignedTo: string;
  hoursUntil: number;
  needsPrep: boolean;
}

// ── Service ───────────────────────────────────────────────────────────────────

const voiceService = {
  preCallBrief: async (params: {
    contactId: string;
    journeyId?: string;
    stage?: number;
    callType?: 'CALL1' | 'CALL2' | 'CALL3';
    callDurationMins?: number;
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/voice-agent/pre-call-brief', params);
    return data as {
      contactId: string; stage: number; callType: string;
      brief: PreCallBrief; model: string;
      meta: { contactName: string; accountName: string; callDurationMins: number };
    };
  },

  coachingPrompts: async (params: {
    contactId: string;
    journeyId?: string;
    stage: number;
    currentTopic?: string;
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/voice-agent/coaching-prompts', params);
    return data as { contactId: string; stage: number; coaching: CoachingPrompts; model: string };
  },

  postCallAnalysis: async (params: {
    contactId: string;
    journeyId?: string;
    stage?: number;
    callType?: 'CALL1' | 'CALL2' | 'CALL3';
    notes: string;
    transcript?: string;
    callDurationMins?: number;
    outcome?: 'positive' | 'neutral' | 'negative' | 'rescheduled' | 'no-show';
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/voice-agent/post-call-analysis', params);
    return data as { contactId: string; stage: number; analysis: PostCallAnalysis; model: string };
  },

  extractStageData: async (params: {
    contactId: string;
    journeyId?: string;
    stage: number;
    rawNotes: string;
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/voice-agent/extract-stage-data', params);
    return data as { contactId: string; stage: number; extracted: ExtractedStageData; model: string };
  },

  nextCallBrief: async (params: {
    contactId: string;
    journeyId?: string;
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/voice-agent/next-call-brief', params);
    return data as {
      contactId: string; stage: number;
      nextCall: NextCallBrief; model: string;
      meta: { contactName: string; accountName: string };
    };
  },

  getScheduled: async () => {
    const { data } = await apiClient.get('/api/voice-agent/scheduled');
    return data as { total: number; data: ScheduledCall[] };
  },
};

export default voiceService;
